import React, { useState } from 'react'
import {
  Row,
  Col,
  Card,
  CardTitle,
  CardBody,
  Collapse,
  ButtonGroup,
  Button,
} from 'reactstrap'
import { CSVLink } from 'react-csv'
import { DateTime } from 'luxon'
import { gql, useQuery, useMutation } from '@apollo/client'
import { useParams, useHistory } from 'react-router-dom'
import {
  Query, Exposure, User, UpdateCaseInput, CaseStatus, CaseLog, CreateNoteInput,
} from 'generated/types'
import Divider from 'components/common/Divider'
import Loader from 'components/common/Loader'
import InfoBlock from 'components/common/InfoBlock'
import PageSubtitle from 'components/common/PageSubtitle'
import PrimaryButton from 'components/common/PrimaryButton'
import SecondaryButton from 'components/common/SecondaryButton'
import { tracingsRoute } from 'helpers/routing'
import LayoutTitle from 'teleporters/LayoutTitle'
import PageTitle from 'components/common/PageTitle'
import { BadgeForStatus } from 'components/common/CaseBadges'
import PageBreadcrumbs from 'components/common/PageBreadcrumbs'
import { DefaultDateRangePicker } from 'components/common/DateRangePicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Table } from 'components/common/Table'
import styled from 'styled-components'
import FormField from 'components/common/FormField'
import { useForm } from 'react-hook-form'
import colors from 'constants/colors'

const CASE_QUERY = gql`
  query Case($id: Int!, $tracingStartTime: DateTime, $tracingEndTime: DateTime) { 
    cases(where: {id: {equals: $id}}) {
      id
      date
      status
      contactCount
      user {
        name
        email
      }
      logs(where: {type: {equals: "NOTE"}}) {
        id
        text
        date
        createdBy {
          name
        }
      }
      tracing(startTime: $tracingStartTime, endTime: $tracingEndTime) {
        startTime
        endTime
        users {
          id
          name
          email
        }
        checkinSessions {
          id
          userCount
          startTime
          endTime
          space {
            id
            name
            location {
              text
            }
          }
        }
        exposures {
          exposedUser {
            id
          }
          checkinSession {
            id
          }
        }
      }
    }
  }
`

const UPDATE_CASE_MUTATION = gql`
  mutation updateCase($input: UpdateCaseInput) {
    updateCase(input: $input) {
      id
      status
    }
  }
`

const StatusButtons = ({ status, updateStatus }: {
  status: CaseStatus,
  updateStatus: (status: CaseStatus) => void
}) => {
  const CloseBtn = () => (
    <SecondaryButton
      title="Close Case"
      icon="times-circle"
      buttonProps={{ className: 'ml-3' }}
      onClick={() => { updateStatus(CaseStatus.Closed) }}
    />
  )

  switch (status) {
    case CaseStatus.Open:
      // TODO: where does 'sent' belong?
      return <CloseBtn />
    case CaseStatus.Closed:
      return (
        <SecondaryButton
          title="Reopen Case"
          icon="plus-circle"
          buttonProps={{ className: 'ml-3' }}
          onClick={() => { updateStatus(CaseStatus.Open) }}
        />
      )
    case CaseStatus.Sent:
      return <CloseBtn />
    default:
      return null
  }
}

export const CasePage: React.FC = () => {
  const params = useParams<{ id: string }>()
  const caseId = parseInt(params.id, 10)
  const history = useHistory()

  const { data, loading, refetch } = useQuery<Query>(CASE_QUERY, {
    variables: {
      id: caseId,
    },
  })

  const [updateCase] = useMutation<UpdateCaseInput>(UPDATE_CASE_MUTATION)

  const updateStatus = (status: CaseStatus) => {
    updateCase({
      variables: {
        input: {
          id: caseId,
          status,
        },
      },
    })
  }

  if (loading) {
    return (
      <Loader />
    )
  }

  if (data?.cases.length !== 1) {
    history.push(tracingsRoute())
    return null
  }

  const caseItem = data.cases[0]
  const tracingStartTime = DateTime.fromISO(caseItem.tracing?.startTime)
  const tracingEndTime = DateTime.fromISO(caseItem.tracing?.endTime)

  const userById: { [key: number]: User } = {}
  caseItem.tracing?.users?.forEach((user) => {
    userById[user.id] = user
  })

  const exposuresBySessionId: { [key: string]: Exposure[] } = {}

  caseItem.tracing?.exposures?.forEach((exposure) => {
    const sessionId = exposure.checkinSession.id
    if (!exposuresBySessionId[sessionId]) {
      exposuresBySessionId[sessionId] = []
    }
    exposuresBySessionId[sessionId].push(exposure)
  })

  const checkinSessions = data?.cases[0].tracing?.checkinSessions || []
  const sortedCheckinSessions = checkinSessions.slice().sort((a, b) => b.startTime - a.endTime)
  const sortedLogs = data.cases[0].logs.slice().sort((a, b) => b.date - a.date)

  const csvProps = {
    filename: [
      'case',
      caseId,
      tracingStartTime.toISODate(),
      tracingEndTime.toISODate(),
    ].join('-'),
    headers: [
      { label: 'Name', key: 'name' },
      { label: 'Email', key: 'email' },
      { label: 'Date', key: 'date' },
      { label: 'Space', key: 'space' },
      { label: 'Location', key: 'location' },
    ],
    data: sortedCheckinSessions.flatMap((session) => (exposuresBySessionId[session.id] || []).map((exposure) => {
      const user = userById[exposure.exposedUser.id]
      return {
        name: user.name,
        email: user.email,
        date: DateTime.fromISO(session.startTime).toUTC().toISODate(),
        space: session.space?.name,
        location: session.space?.location?.text,
      }
    })),
  }

  return (
    <>
      <LayoutTitle.Source>
        <PageBreadcrumbs routeTo="/tracing">
          Contact Tracing
        </PageBreadcrumbs>
        <PageTitle>
          {caseItem.user?.name}
          <BadgeForStatus
            pill
            status={caseItem.status}
            style={{
              display: 'inline',
              float: 'none',
              marginLeft: 20,
              verticalAlign: 'middle',
            }}
          />
        </PageTitle>
      </LayoutTitle.Source>

      <Row>
        <Col xs="10">
          <CSVLink {...csvProps} target="_blank">
            <PrimaryButton
              title="Export Contact CSV"
              icon="file-export"
            />
          </CSVLink>

          <StatusButtons status={caseItem.status} updateStatus={updateStatus} />
        </Col>
      </Row>

      <Row>
        <Col xs="8">
          <InfoBlock
            title="Reported Date"
            subtitle={DateTime.fromISO(caseItem.date).toFormat('MMMM d')}
            icon="temperature-high"
          />
          {/* <InfoBlock
          title="Phone"
          subtitle="(123) 456-789"
          icon="phone"
        /> */}
          <InfoBlock
            title="Email"
            subtitle={caseItem.user.email}
            icon="envelope"
          />
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col>
          <PageSubtitle>Checkin History</PageSubtitle>
          <DefaultDateRangePicker
            startDate={tracingStartTime.toJSDate()}
            endDate={tracingEndTime.toJSDate()}
            showClearDates={false}
            disableFutureDates={true}
            onClose={({ startDate, endDate }) => {
              const tracingStartTime = startDate && DateTime.fromJSDate(startDate).toLocal().startOf('day').toJSDate()
              const tracingEndTime = endDate && DateTime.fromJSDate(endDate).toLocal().endOf('day').toJSDate()
              refetch({ tracingStartTime, tracingEndTime })
            }}
          />
        </Col>
      </Row>

      {sortedCheckinSessions.length === 0
        && (
          <Row style={{ marginTop: 30 }}>
            <Col>
              No recorded checkins between
              {tracingStartTime.toFormat('MM/dd/yyyy')} and {tracingEndTime.toFormat('MM/dd/yyyy')}
            </Col>
          </Row>
        )}
      {sortedCheckinSessions.length > 0
        && (
          <Row>
            <Col className="mt-4" xs="12" sm="12" md="12" lg="8">
              <Table>
                <thead>
                  <tr>
                    <th><FontAwesomeIcon icon="map-marker-alt" className="mr-1" /> Space</th>
                    <th><FontAwesomeIcon icon="map" className="mr-1" /> Location</th>
                    <th><FontAwesomeIcon icon="calendar" className="mr-1" /> Date</th>
                    <th><FontAwesomeIcon icon="user-friends" className="mr-1" /> Contacts</th>
                    <th aria-label="Filter CSV" />
                  </tr>
                </thead>
                <tbody>
                  {sortedCheckinSessions.map((session) => (
                    <tr>
                      <td>{session.space?.name}</td>
                      <td>{session.space?.location?.text}</td>
                      <td>{DateTime.fromISO(session.startTime).toUTC().toFormat('MMMM d')}</td>
                      <td>{session.userCount - 1}</td>
                      <td />
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
      <Row>
        <Col className="mt-4" xs="8">
          <NotesList logs={sortedLogs} caseId={caseId} />
        </Col>
      </Row>

    </>
  )
}

const CREATE_NOTE_MUTATION = gql`
  mutation createCaseNote($input: CreateNoteInput!) {
    createCaseNote(input: $input) {
      id
    }
  }
`

interface CreateNoteFormData {
  note: string
}

const NotesList: React.FC<{ logs: CaseLog[], caseId: number }> = ({ logs, caseId }) => {
  const [showForm, setShowForm] = useState(false)
  const {
    handleSubmit, register, reset,
  } = useForm<CreateNoteFormData>()
  const [createNote] = useMutation(CREATE_NOTE_MUTATION)

  const onSubmit = async ({ note }: CreateNoteFormData) => {
    const input: CreateNoteInput = {
      notes: note,
      caseId,
    }

    await createNote({
      variables: {
        input,
      },
      // TODO: we can be more efficient here,
      // maybe use a separate query for the logs as part of this component
      // alternatively insert directly into the cache
      refetchQueries: ['Case'],
    })
    setShowForm(false)
    reset()
  }

  return (
    <>
      <PageSubtitle>Activity</PageSubtitle>
      {!showForm
        && (
          <SecondaryButton
            icon="plus-circle"
            title="Add Comment"
            buttonProps={{ className: 'mb-3' }}
            onClick={() => { setShowForm((show) => !show) }}
          />
        )}
      <Collapse isOpen={showForm}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormField
            name="note"
            type="textarea"
            label="Note"
            innerRef={register()}
          />
          <ButtonGroup className="mb-3">
            <SecondaryButton
              icon="plus-circle"
              title="Add Note"
              buttonProps={{ type: 'submit', className: 'mr-3' }}
            />
            <Button
              color="link"
              onClick={() => {
                setShowForm(false)
                reset()
              }}
            >Cancel
            </Button>
          </ButtonGroup>
        </form>
      </Collapse>
      {logs.map((ld) => <LogEntry log={ld} />)}
    </>
  )
}

const LogCard = styled(Card)`
  padding: 0;
  margin-top: 5px;
  margin-bottom: 14px;
  border: 0;

  .card-title {
    margin-bottom: -5px;
  }
`

const LogCardBody = styled(CardBody)`
  padding: 10px 12px;
  background-color: ${colors.inputGrey};
  border-radius: 14px;
  margin-left: 40px;
`

const UserName = styled.strong`
  font-family: Poppins;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.black};
  letter-spacing: 0;
  margin-right: 8px;
`

const Date = styled.strong`
  font-family: Poppins;
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0;
  color: ${colors.grey};
`

const Avatar = styled.div`
  font-family: Poppins;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: 0;
  text-align: center;
  color: ${colors.black};
  background: #EDEEF0;
  height: 32px;
  width: 32px;
  border-radius: 20px;
  display: inline-block;
  float: left;
  padding-top: 5px;
  padding-left: 1px;
  margin-right: 14px;
  margin-top: 4px;
`

const LogEntry: React.FC<{ log: CaseLog }> = ({
  log: {
    id, createdBy, text, date,
  },
}) => {
  const firstLast = createdBy && createdBy.name ? createdBy.name.match(/\b(\w)/g) : ''
  const userInitials = firstLast && firstLast.join('')

  return (
    <LogCard key={`caselog-${id}`}>
      <CardTitle>
        <Avatar>{userInitials}</Avatar>
        <UserName className="user-name">{createdBy.name}</UserName>
        <Date>{DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_SHORT)}</Date>
      </CardTitle>
      <LogCardBody>
        {text}
      </LogCardBody>
    </LogCard>
  )
}

export default CasePage
